import React, { useState } from 'react';
import { Alert, Box, Button, Chip, Divider, Paper, Switch, Typography } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { OrgInfo } from '../context/UserContext';
import { ROLE_LABELS, useOrgApi } from '../utils/orgApi';
import { MFA_ACR } from '../utils/signInPolicy';

/**
 * Team page > Security (enterprise batch 10), firm admins only.
 *
 * Two-factor: the admin's switch. Turning it on needs this sign-in to have
 * used two-factor (the server refuses otherwise), so the page offers to sign
 * in with two-factor first. Until UnderwritRE switches two-factor on for the
 * platform (org.mfa_available, a paid Auth0 plan), the row only says it is
 * available on request, and the "This sign-in" row is hidden.
 *
 * SSO and company domains: shown read-only. They depend on setup in Auth0,
 * which UnderwritRE does for the firm (scripts/auth0/README.md).
 */

interface Props {
  org: OrgInfo;
  onChanged: () => Promise<void>;
  flash: (msg: string) => void;
  fail: (msg?: string) => void;
}

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Box>{children}</Box>
  </>
);

const FirmSecurity: React.FC<Props> = ({ org, onChanged, flash, fail }) => {
  const api = useOrgApi();
  const { loginWithRedirect } = useAuth0();
  const [saving, setSaving] = useState(false);
  const signIn = org.my_sign_in;
  const usedMfa = Boolean(signIn?.mfa);

  const verifyMfa = () =>
    loginWithRedirect({
      appState: { returnTo: '/team' },
      authorizationParams: { acr_values: MFA_ACR },
    });

  const setMfa = async (on: boolean) => {
    setSaving(true);
    const r = await api('PATCH', '/org', { require_mfa: on });
    setSaving(false);
    if (!r.ok) return fail(r.error);
    flash(on
      ? 'Two-factor sign-in is now required. Everyone in the firm is asked for a second factor at their next sign-in.'
      : 'Two-factor sign-in is now optional.');
    await onChanged();
  };

  const domains = org.domains || [];
  const mfaOffered = Boolean(org.mfa_available) || Boolean(org.require_mfa);

  return (
    <Paper variant="outlined" sx={{ p: 3, mt: 4 }}>
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>Security</Typography>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' }, rowGap: 2, columnGap: 2, alignItems: 'start' }}>
        <Row label="Two-factor sign-in">
          {!mfaOffered ? (
            <Typography variant="body2" color="text.secondary">
              Available for enterprise firms: everyone signs in with a code from an authenticator app as well as
              their password. Email info@underwritre.com to turn it on for your firm.
            </Typography>
          ) : (
          <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Switch
              checked={Boolean(org.require_mfa)}
              disabled={saving || (!org.require_mfa && !usedMfa)}
              onChange={(e) => setMfa(e.target.checked)}
              inputProps={{ 'aria-label': 'require two-factor sign-in' }}
            />
            <Typography variant="body1">
              {org.require_mfa ? 'Required for everyone in the firm' : 'Optional'}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            When required, everyone signs in with a code from an authenticator app (Google Authenticator,
            1Password, Authy) as well as their password. They set it up the first time they are asked.
          </Typography>
          {!org.require_mfa && !usedMfa && (
            <Alert severity="info" sx={{ mt: 1.5 }} action={
              <Button color="inherit" size="small" onClick={verifyMfa}>Sign in with two-factor</Button>
            }>
              To turn this on, first sign in with two-factor yourself. That proves it works before
              anyone else is asked for it.
            </Alert>
          )}
          </>
          )}
        </Row>

        <Row label="Single sign-on">
          {org.sso_connection ? (
            <>
              <Typography variant="body1">
                Through your company login (<code>{org.sso_connection}</code>)
                {org.sso_enforced ? ', required for everyone in the firm' : ', optional'}
              </Typography>
              {org.sso_enforced && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Signing in with a password or Google is refused; people are sent to the company login.
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Not set up. To sign in with Okta, Microsoft Entra ID (Azure AD), Google Workspace or any SAML
              provider, email info@underwritre.com and we will connect it for your firm.
            </Typography>
          )}
        </Row>

        <Row label="Company domains">
          {domains.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              None. With a confirmed domain, colleagues who sign up with their work address join the firm
              automatically. UnderwritRE confirms domains for you, usually while setting up single sign-on.
            </Typography>
          ) : (
            <>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {domains.map((d) => (
                  <Chip
                    key={d.id}
                    label={`${d.domain}${d.auto_join ? `, joins as ${ROLE_LABELS[d.default_role] || d.default_role}` : ', no auto-join'}`}
                    size="small"
                  />
                ))}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                New people who sign up with these addresses join the firm automatically, if a seat is free.
              </Typography>
            </>
          )}
        </Row>

        {mfaOffered && (
        <Row label="This sign-in">
          {signIn && signIn.action_claims ? (
            <Typography variant="body2">
              {signIn.connection || 'unknown'}, two-factor: {signIn.mfa ? 'yes' : 'no'}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Action not updated yet (UnderwritRE setup). Two-factor cannot be required until it is.
            </Typography>
          )}
        </Row>
        )}
      </Box>
    </Paper>
  );
};

export default FirmSecurity;
